import React, { FC, memo } from 'react';
import styled from '@emotion/styled';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { Box } from '~Styles';
import RoundedIcon from '../RoundedIcon';
import { SettingBlockItemType } from '.';
import Toggle from '../Toggle';
import { useDispatch, useI18n } from '~Hooks';
import * as actions from '~actions';

const Wrapper = styled.div<{ onClick?: () => void; beforeColor?: string; disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--colorBlueGray1000);
  transition: background 0.1s ease-in-out;
  position: relative;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  ${({ onClick, disabled }) =>
    onClick &&
    !disabled &&
    `
    &:active {
      background: var(--colorBlueGray1000);
    }
    `}

  &:last-child {
    border-bottom: none;
  }

  &:before {
    content: '';
    width: 4px;
    height: 100%;
    background-color: ${({ beforeColor }) => beforeColor || 'transparent'};
    position: absolute;
    left: 0;
  }
`;

const SettingsBlockMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  flex: 1 1 auto;
`;

const Column = styled.div<{ right?: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  align-items: ${({ right }) => (right ? 'flex-end' : 'flex-start')};
`;

const SettingsBlockAction = styled.div`
  display: flex;
  align-items: center;
  margin-left: 16px;
`;

interface Props {
  item: SettingBlockItemType;
}

const SettingsBlockItem: FC<Props> = ({ item }) => {
  const dispatch = useDispatch();
  const { i18n } = useI18n();

  const handleDisabledClick = () => {
    if (item?.disabled && item?.disabledToast) {
      dispatch(actions.addToast(item.disabledToast, true));
    }

    if (item.isRemotelyControlled) {
      dispatch(actions.addToast(i18n('Settings.LockedByBaemingo.Toast'), true));
    }
  };

  return (
    <Wrapper
      onClick={
        item.disabled || item.isRemotelyControlled ? handleDisabledClick : item.action?.onClick
      }
      disabled={item.disabled || item.isRemotelyControlled}
      beforeColor={item.prefixColor}>
      <SettingsBlockMeta>
        <Column>
          <Box display="flex" alignItems="center">
            {item.prefixIcon && <RoundedIcon margin="0 16px 0 0" {...item.prefixIcon} />}
            <Heading level={item.bigTitle ? 4 : 5} color="var(--colorBlueGray50)">
              {item.title}
            </Heading>
          </Box>
          {item.additionalText?.left && (
            <Paragraph
              level={5}
              color="var(--colorBlueGray200)"
              top={8}
              left={item.prefixIcon ? 48 : 0}>
              {item.additionalText.left || ''}
            </Paragraph>
          )}
        </Column>
        <Column right>
          {item.description && (
            <Paragraph level={4} color="var(--colorWhite)">
              {item.description}
            </Paragraph>
          )}
          {item.additionalText?.right && (
            <Paragraph level={5} color="var(--colorBlueGray200)" top={8}>
              {item.additionalText.right || ''}
            </Paragraph>
          )}
        </Column>
      </SettingsBlockMeta>
      {item.action && (
        <SettingsBlockAction>
          {item.action.title && (
            <Paragraph level={4} color="var(--colorStatusBlue)">
              {item.action.title || ''}
            </Paragraph>
          )}
          {item.action.isToggle && <Toggle value={item.action.selected || false} />}
          {!item.action.isToggle && item.action.icon && (
            <RoundedIcon
              {...item.action.icon}
              bgColor="transparent"
              color={item.action.selected ? item.action.selectedColor || 'uiGreen' : undefined}
            />
          )}
        </SettingsBlockAction>
      )}
    </Wrapper>
  );
};

export default memo(SettingsBlockItem);
